import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  User, 
  Client, 
  CheckIn, 
  Material, 
  MaterialAssignment,
  ContactSubmission, 
  ProgressData, 
  AnalyticsData,
  PaginatedResponse,
  LoginForm,
  CheckInForm,
  ContactForm,
  WeeklyCheckIn,
  WeeklyCheckInSummary,
  WeeklyCheckInStats,
  StripeProduct,
  StripePrice,
  SubscriptionOption,
  Subscription,
  SubscriptionEvent,
  SyncResult,
  ClientInvitation,
  ClientInvitationCreate,
  MaterialAssignmentCreate,
  CheckInQuestion,
  CheckInQuestionCreate,
  GroupTrainingPlan,
  GroupWorkoutPlanStatus,
  GroupZoomSession,
  Exercise,
  TrainingPlanExercise
} from '../types';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://vectorperformance.ie.ie/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh and errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Only redirect to login if we're not already on the login page
          // and if this is not an auth-related request
          const isAuthRequest = error.config?.url?.includes('/auth/');
          const isLoginPage = window.location.pathname === '/login';
          
          if (!isAuthRequest && !isLoginPage) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async login(credentials: LoginForm): Promise<{ access: string; refresh: string; user: User }> {
    // First, get JWT tokens
    const tokenResponse: AxiosResponse<{ access: string; refresh: string }> = await this.api.post('/auth/login/', credentials);
    
    // Store tokens temporarily to make the user request
    const { access, refresh } = tokenResponse.data;
    const originalToken = localStorage.getItem('access_token');
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    
    try {
      // Get user data using the new token
      const userResponse: AxiosResponse<{ success: boolean; data: User; message: string }> = await this.api.get('/auth/user/');
      
      if (!userResponse.data.success || !userResponse.data.data) {
        throw new Error('Failed to get user data after login');
      }
      
      return {
        access,
        refresh,
        user: userResponse.data.data
      };
    } catch (error) {
      // Restore original token if user fetch fails
      if (originalToken) {
        localStorage.setItem('access_token', originalToken);
      } else {
        localStorage.removeItem('access_token');
      }
      localStorage.removeItem('refresh_token');
      throw error;
    }
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout/');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  async refreshToken(): Promise<{ access: string }> {
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) throw new Error('No refresh token available');
    
    const response: AxiosResponse<{ access: string }> = await this.api.post('/auth/token/refresh/', { refresh });
    return response.data;
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await this.api.post('/auth/password/change/', { old_password: oldPassword, new_password: newPassword });
  }

  async setPassword(email: string, password: string, confirmPassword: string): Promise<void> {
    await this.api.post('/users/set-password/', { 
      email, 
      password, 
      confirm_password: confirmPassword 
    });
  }

  async requestPasswordReset(email: string): Promise<void> {
    await this.api.post('/users/password-reset/', { email });
  }

  async confirmPasswordReset(token: string, email: string, password: string, confirmPassword: string): Promise<void> {
    await this.api.post('/users/password-reset/confirm/', {
      token,
      email,
      password,
      confirm_password: confirmPassword
    });
  }

  // User Management
  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<{ success: boolean; data: User; message: string }> = await this.api.get('/auth/user/');
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get current user data');
    }
    return response.data.data;
  }

  async updateProfile(userData: Partial<User>): Promise<User> {
    const response: AxiosResponse<User> = await this.api.put('/auth/user/', userData);
    return response.data;
  }

  // User Impersonation (Superuser only)
  async startImpersonation(userId: number): Promise<{ access: string; refresh: string; impersonated_user: User; original_user: User }> {
    const response: AxiosResponse<{ success: boolean; data: { access: string; refresh: string; impersonated_user: User; original_user: User }; message: string }> = 
      await this.api.post('/users/impersonate/start/', { user_id: userId });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to start impersonation');
    }
    return response.data.data;
  }

  async stopImpersonation(): Promise<{ access: string; refresh: string; user: User }> {
    const response: AxiosResponse<{ success: boolean; data: { access: string; refresh: string; user: User }; message: string }> = 
      await this.api.post('/users/impersonate/stop/');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to stop impersonation');
    }
    return response.data.data;
  }

  async getAllUsers(): Promise<User[]> {
    const response: AxiosResponse<{ success: boolean; data: PaginatedResponse<User>; message: string }> = 
      await this.api.get('/users/');
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get users');
    }
    if (!response.data.data || !response.data.data.results) {
      throw new Error('Invalid response format from server');
    }
    return response.data.data.results || [];
  }

  // Client Management (Trainer only)
  async getClients(page = 1, search = '', status = ''): Promise<PaginatedResponse<Client>> {
    const params = new URLSearchParams();
    if (page > 1) params.append('page', page.toString());
    if (search) params.append('search', search);
    if (status) params.append('status', status);

    const response: AxiosResponse<{ success: boolean; data: PaginatedResponse<Client>; message: string }> = await this.api.get(`/clients/?${params}`);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get clients data');
    }
    return response.data.data;
  }

  async getClient(id: number): Promise<Client> {
    const response: AxiosResponse<{ success: boolean; data: Client; message: string }> = await this.api.get(`/clients/${id}/`);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get client data');
    }
    return response.data.data;
  }

  async getCurrentClientProfile(): Promise<Client> {
    const response: AxiosResponse<{ success: boolean; data: Client; message: string }> = await this.api.get('/clients/dashboard/');
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get client profile');
    }
    return response.data.data;
  }

  async createClient(clientData: Partial<Client>): Promise<Client> {
    const response: AxiosResponse<{ success: boolean; data: Client; message: string }> = await this.api.post('/clients/', clientData);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to create client');
    }
    return response.data.data;
  }

  async updateClient(id: number, clientData: Partial<Client>): Promise<Client> {
    const response: AxiosResponse<{ success: boolean; data: Client; message: string }> = await this.api.put(`/clients/${id}/`, clientData);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to update client');
    }
    return response.data.data;
  }

  async deleteClient(id: number): Promise<void> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.delete(`/clients/${id}/`);
    if (!response.data.success) {
      throw new Error('Failed to delete client');
    }
  }

  // Client Invitations (Trainer only)
  async getClientInvitations(page = 1): Promise<PaginatedResponse<ClientInvitation>> {
    const params = new URLSearchParams();
    if (page > 1) params.append('page', page.toString());

    const response: AxiosResponse<{ success: boolean; data: PaginatedResponse<ClientInvitation>; message: string }> = await this.api.get(`/clients/invitations/?${params}`);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get client invitations data');
    }
    return response.data.data;
  }

  async getClientInvitation(id: number): Promise<ClientInvitation> {
    const response: AxiosResponse<{ success: boolean; data: ClientInvitation; message: string }> = await this.api.get(`/clients/invitations/${id}/`);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get client invitation data');
    }
    return response.data.data;
  }

  async createClientInvitation(invitationData: ClientInvitationCreate): Promise<ClientInvitation> {
    const response: AxiosResponse<{ success: boolean; data: ClientInvitation; message: string }> = await this.api.post('/clients/invitations/', invitationData);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to create client invitation');
    }
    return response.data.data;
  }

  async updateClientInvitation(id: number, invitationData: Partial<ClientInvitation>): Promise<ClientInvitation> {
    const response: AxiosResponse<{ success: boolean; data: ClientInvitation; message: string }> = await this.api.put(`/clients/invitations/${id}/`, invitationData);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to update client invitation');
    }
    return response.data.data;
  }

  async deleteClientInvitation(id: number): Promise<void> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.delete(`/clients/invitations/${id}/`);
    if (!response.data.success) {
      throw new Error('Failed to delete client invitation');
    }
  }

  async resendInvitationEmail(invitationId: number): Promise<void> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.post(`/clients/invitations/${invitationId}/resend/`);
    if (!response.data.success) {
      throw new Error('Failed to resend invitation email');
    }
  }

  // Check-ins
  async getCheckIns(page = 1, clientId?: number, dateFrom?: string, dateTo?: string): Promise<PaginatedResponse<CheckIn>> {
    const params = new URLSearchParams();
    if (page > 1) params.append('page', page.toString());
    if (clientId) params.append('client', clientId.toString());
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);

    const response: AxiosResponse<PaginatedResponse<CheckIn>> = await this.api.get(`/checkins/?${params}`);
    return response.data;
  }

  async getClientCheckIns(clientId: number): Promise<CheckIn[]> {
    const response: AxiosResponse<{ success: boolean; data: any; message: string }> = await this.api.get(`/checkins/trainer/clients/${clientId}/checkins/`);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get client check-ins');
    }
    return response.data.data.checkins || [];
  }

  async submitCheckIn(checkInData: CheckInForm): Promise<CheckIn> {
    const response: AxiosResponse<CheckIn> = await this.api.post('/checkins/', checkInData);
    return response.data;
  }

  async updateCheckIn(id: number, checkInData: Partial<CheckInForm>): Promise<CheckIn> {
    const response: AxiosResponse<CheckIn> = await this.api.put(`/checkins/${id}/`, checkInData);
    return response.data;
  }

  async deleteCheckIn(id: number): Promise<void> {
    await this.api.delete(`/checkins/${id}/`);
  }

  // Weekly Check-ins
  async getWeeklyCheckIns(page = 1): Promise<PaginatedResponse<WeeklyCheckInSummary>> {
    const params = new URLSearchParams();
    if (page > 1) params.append('page', page.toString());

    const response: AxiosResponse<{ success: boolean; data: PaginatedResponse<WeeklyCheckInSummary>; message: string }> = 
      await this.api.get(`/checkins/weekly/?${params}`);
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get weekly check-ins');
    }
    return response.data.data;
  }

  async getWeeklyCheckIn(id: number): Promise<WeeklyCheckIn> {
    const response: AxiosResponse<{ success: boolean; data: WeeklyCheckIn; message: string }> = 
      await this.api.get(`/checkins/weekly/${id}/`);
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get weekly check-in');
    }
    return response.data.data;
  }

  async submitWeeklyCheckIn(checkInData: Partial<Omit<WeeklyCheckIn, 'id' | 'client' | 'submitted_at' | 'average_consistency_rating' | 'traffic_light_color'>>): Promise<WeeklyCheckIn> {
    const response: AxiosResponse<{ success: boolean; data: WeeklyCheckIn; message: string }> = 
      await this.api.post('/checkins/weekly/', checkInData);
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to submit weekly check-in');
    }
    return response.data.data;
  }

  async updateWeeklyCheckIn(id: number, checkInData: Partial<WeeklyCheckIn>): Promise<WeeklyCheckIn> {
    const response: AxiosResponse<{ success: boolean; data: WeeklyCheckIn; message: string }> = 
      await this.api.put(`/checkins/weekly/${id}/`, checkInData);
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to update weekly check-in');
    }
    return response.data.data;
  }

  async deleteWeeklyCheckIn(id: number): Promise<void> {
    const response: AxiosResponse<{ success: boolean; message: string }> = 
      await this.api.delete(`/checkins/weekly/${id}/`);
    
    if (!response.data.success) {
      throw new Error('Failed to delete weekly check-in');
    }
  }

  async getClientWeeklyCheckIns(clientId: number): Promise<WeeklyCheckInSummary[]> {
    const response: AxiosResponse<{ success: boolean; data: WeeklyCheckInSummary[]; message: string }> = 
      await this.api.get(`/checkins/weekly/client/${clientId}/`);
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get client weekly check-ins');
    }
    return response.data.data;
  }

  async getWeeklyCheckInStats(clientId?: number): Promise<WeeklyCheckInStats> {
    const params = new URLSearchParams();
    if (clientId) params.append('client_id', clientId.toString());

    const response: AxiosResponse<{ success: boolean; data: WeeklyCheckInStats; message: string }> = 
      await this.api.get(`/checkins/weekly/stats/?${params}`);
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get weekly check-in stats');
    }
    return response.data.data;
  }

  // Check-In Questions (Trainer only)
  async getCheckInQuestions(): Promise<CheckInQuestion[]> {
    const response: AxiosResponse<{ success: boolean; data: CheckInQuestion[]; message: string }> = 
      await this.api.get('/checkins/questions/');
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get check-in questions');
    }
    return response.data.data;
  }

  async getCheckInQuestion(id: number): Promise<CheckInQuestion> {
    const response: AxiosResponse<{ success: boolean; data: CheckInQuestion; message: string }> = 
      await this.api.get(`/checkins/questions/${id}/`);
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get check-in question');
    }
    return response.data.data;
  }

  async createCheckInQuestion(questionData: CheckInQuestionCreate): Promise<CheckInQuestion> {
    const response: AxiosResponse<{ success: boolean; data: CheckInQuestion; message: string }> = 
      await this.api.post('/checkins/questions/', questionData);
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to create check-in question');
    }
    return response.data.data;
  }

  async updateCheckInQuestion(id: number, questionData: Partial<CheckInQuestionCreate>): Promise<CheckInQuestion> {
    const response: AxiosResponse<{ success: boolean; data: CheckInQuestion; message: string }> = 
      await this.api.put(`/checkins/questions/${id}/`, questionData);
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to update check-in question');
    }
    return response.data.data;
  }

  async deleteCheckInQuestion(id: number): Promise<void> {
    const response: AxiosResponse<{ success: boolean; message: string }> = 
      await this.api.delete(`/checkins/questions/${id}/`);
    
    if (!response.data.success) {
      throw new Error('Failed to delete check-in question');
    }
  }

  async getActiveCheckInQuestions(): Promise<CheckInQuestion[]> {
    const response: AxiosResponse<{ success: boolean; data: CheckInQuestion[]; message: string }> = 
      await this.api.get('/checkins/questions/active/');
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get active check-in questions');
    }
    return response.data.data;
  }

  async getCurrentWeekCheckIn(clientId?: number): Promise<WeeklyCheckIn | { exists: false; week_start_date: string; message: string }> {
    const params = new URLSearchParams();
    if (clientId) params.append('client_id', clientId.toString());

    const response: AxiosResponse<{ success: boolean; data: WeeklyCheckIn | { exists: false; week_start_date: string; message: string }; message: string }> = 
      await this.api.get(`/checkins/weekly/current/?${params}`);
    
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get current week check-in');
    }
    return response.data.data;
  }

  // Trainer-specific check-in endpoints
  async getTrainerRecentCheckIns(): Promise<WeeklyCheckInSummary[]> {
    const response: AxiosResponse<{ success: boolean; data: WeeklyCheckInSummary[]; message: string }> = await this.api.get('/checkins/trainer/recent/');
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get recent check-ins');
    }
    return response.data.data;
  }

  async getTrainerClientList(): Promise<{
    id: number;
    user: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      username: string;
    };
    checkin_count: number;
    latest_checkin: WeeklyCheckInSummary | null;
    last_checkin_date: string | null;
  }[]> {
    const response: AxiosResponse<{ success: boolean; data: any[]; message: string }> = await this.api.get('/checkins/trainer/clients/');
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get client list');
    }
    return response.data.data;
  }

  async getTrainerClientCheckIns(clientId: number): Promise<{
    client: {
      id: number;
      user: {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
        username: string;
      };
    };
    checkins: WeeklyCheckInSummary[];
    total_checkins: number;
  }> {
    const response: AxiosResponse<{ success: boolean; data: any; message: string }> = await this.api.get(`/checkins/trainer/clients/${clientId}/checkins/`);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get client check-ins');
    }
    return response.data.data;
  }

  // Materials Management
  async getMaterials(page = 1, assignedToMe = false): Promise<PaginatedResponse<Material>> {
    const params = new URLSearchParams();
    if (page > 1) params.append('page', page.toString());
    if (assignedToMe) params.append('assigned_to_me', 'true');

    const response: AxiosResponse<{ success: boolean; data: PaginatedResponse<Material>; message: string }> = await this.api.get(`/materials/materials/?${params}`);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get materials data');
    }
    return response.data.data;
  }

  async getMaterial(id: number): Promise<Material> {
    const response: AxiosResponse<{ success: boolean; data: Material; message: string }> = await this.api.get(`/materials/materials/${id}/`);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get material data');
    }
    return response.data.data;
  }

  async uploadMaterial(formData: FormData): Promise<Material> {
    const response: AxiosResponse<{ success: boolean; data: Material; message: string }> = await this.api.post('/materials/materials/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to upload material');
    }
    return response.data.data;
  }

  async updateMaterial(id: number, materialData: Partial<Material>): Promise<Material> {
    const response: AxiosResponse<{ success: boolean; data: Material; message: string }> = await this.api.put(`/materials/materials/${id}/`, materialData);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to update material');
    }
    return response.data.data;
  }

  async deleteMaterial(id: number): Promise<void> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.delete(`/materials/materials/${id}/`);
    if (!response.data.success) {
      throw new Error('Failed to delete material');
    }
  }

  async assignMaterial(materialId: number, assignmentData: MaterialAssignmentCreate): Promise<void> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.post(`/materials/materials/${materialId}/assign/`, assignmentData);
    if (!response.data.success) {
      throw new Error('Failed to assign material');
    }
  }

  async downloadMaterial(id: number): Promise<Blob | { download_url: string; filename: string }> {
    const response = await this.api.get(`/materials/materials/${id}/download/`);
    
    // Check if this is an S3 file with direct URL
    if (response.data.success && response.data.data && response.data.data.download_url) {
      return response.data.data;
    }
    
    // Fallback to blob download for local files
    const blobResponse = await this.api.get(`/materials/materials/${id}/download/`, {
      responseType: 'blob',
    });
    return blobResponse.data;
  }

  async getMaterialAssignments(page = 1): Promise<PaginatedResponse<MaterialAssignment>> {
    const params = new URLSearchParams();
    if (page > 1) params.append('page', page.toString());

    const response: AxiosResponse<{ success: boolean; data: PaginatedResponse<MaterialAssignment>; message: string }> = await this.api.get(`/materials/assignments/?${params}`);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get material assignments');
    }
    return response.data.data;
  }

  async getAvailableClients(): Promise<Client[]> {
    const response: AxiosResponse<{ success: boolean; data: Client[]; message: string }> = await this.api.get('/materials/materials/available_clients/');
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get available clients');
    }
    return response.data.data;
  }

  async getMaterialStats(): Promise<{
    total_materials: number;
    assigned_to_all: number;
    assigned_to_specific: number;
    unassigned: number;
  }> {
    const response: AxiosResponse<{ success: boolean; data: any; message: string }> = await this.api.get('/materials/stats/');
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get material stats');
    }
    return response.data.data;
  }

  // Progress & Analytics
  async getClientProgress(clientId: number): Promise<ProgressData> {
    const response: AxiosResponse<{ success: boolean; data: ProgressData; message: string }> = await this.api.get(`/analytics/client-progress/${clientId}/`);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get client progress data');
    }
    return response.data.data;
  }

  async getTrainerAnalytics(): Promise<AnalyticsData> {
    const response: AxiosResponse<{ success: boolean; data: AnalyticsData; message: string }> = await this.api.get('/analytics/trainer-dashboard/');
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get trainer analytics data');
    }
    return response.data.data;
  }

  // Contact & Public
  async submitContactForm(contactData: ContactForm): Promise<ContactSubmission> {
    const response: AxiosResponse<ContactSubmission> = await this.api.post('/public/contact/', contactData);
    return response.data;
  }

  async getContactSubmissions(page = 1): Promise<PaginatedResponse<ContactSubmission>> {
    const params = new URLSearchParams();
    if (page > 1) params.append('page', page.toString());

    const response: AxiosResponse<PaginatedResponse<ContactSubmission>> = await this.api.get(`/public/contact/?${params}`);
    return response.data;
  }

  // Stripe Integration
  async createGroupCoachingCheckoutSession(): Promise<{ checkout_url: string }> {
    const response: AxiosResponse<{ checkout_url: string }> = await this.api.post(
      '/subscriptions/public/checkout-session/'
    );
    return response.data;
  }

  async getGroupCoachingCheckoutStatus(sessionId: string): Promise<{
    status: 'pending' | 'paid' | 'failed' | 'canceled';
    bootstrap_token?: string;
    next?: string;
  }> {
    const response: AxiosResponse<{
      status: 'pending' | 'paid' | 'failed' | 'canceled';
      bootstrap_token?: string;
      next?: string;
    }> = await this.api.get(`/subscriptions/public/checkout-status/?session_id=${encodeURIComponent(sessionId)}`);
    return response.data;
  }

  async bootstrapFromCheckout(bootstrapToken: string): Promise<{ access: string; refresh: string; user: User }> {
    const response: AxiosResponse<{ access: string; refresh: string; user: User }> = await this.api.post(
      '/subscriptions/public/bootstrap/',
      { bootstrap_token: bootstrapToken }
    );
    return response.data;
  }

  async submitGroupOnboarding(responses: Record<string, any>): Promise<{ success: boolean; data: any; message: string }> {
    const response: AxiosResponse<{ success: boolean; data: any; message: string }> = await this.api.post(
      '/clients/group-onboarding/',
      responses
    );
    return response.data;
  }

  async getGroupWorkoutPlanStatus(): Promise<GroupWorkoutPlanStatus> {
    const response: AxiosResponse<{ success: boolean; data: GroupWorkoutPlanStatus; message: string }> =
      await this.api.get('/clients/group-onboarding/status/');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to load workout plan status');
    }
    return response.data.data;
  }

  async regenerateGroupWorkoutPlan(): Promise<{
    workout_plan: GroupWorkoutPlanStatus['workout_plan'];
    eligibility: GroupWorkoutPlanStatus['eligibility'];
  }> {
    const response: AxiosResponse<{
      success: boolean;
      data: { workout_plan: GroupWorkoutPlanStatus['workout_plan']; eligibility: GroupWorkoutPlanStatus['eligibility'] };
      message: string;
    }> = await this.api.post('/clients/group-onboarding/regenerate-workout-plan/');

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to regenerate workout plan');
    }

    return response.data.data;
  }

  // --- Trainer workout plan review API ---

  async getWorkoutPlanReviewQueue(): Promise<{
    count: number;
    results: Array<{
      onboarding_id: number;
      client_id: number;
      client_name: string;
      client_email: string;
      review_status: string;
      generated_at: string | null;
      material: { material_id: number; title: string; file_url: string } | null;
    }>;
  }> {
    const response: AxiosResponse<{
      success: boolean;
      data: {
        count: number;
        results: Array<{
          onboarding_id: number;
          client_id: number;
          client_name: string;
          client_email: string;
          review_status: string;
          generated_at: string | null;
          material: { material_id: number; title: string; file_url: string } | null;
        }>;
      };
      message: string;
    }> = await this.api.get('/clients/group-onboarding/review-queue/');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch review queue');
    }
    return response.data.data;
  }

  async getWorkoutPlanReviewDetail(onboardingId: number): Promise<{
    onboarding_id: number;
    client_id: number;
    client_name: string;
    client_email: string;
    review_status: string;
    generation_status: string;
    generated_at: string | null;
    approved_at: string | null;
    trainer_context: string;
    review_notes: string;
    draft_material: { material_id: number; title: string; file_url: string } | null;
    approved_material: { material_id: number; title: string; file_url: string } | null;
  }> {
    const response: AxiosResponse<{
      success: boolean;
      data: any;
      message: string;
    }> = await this.api.get(`/clients/group-onboarding/${onboardingId}/review/`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch review detail');
    }
    return response.data.data;
  }

  async approveWorkoutPlan(onboardingId: number, notes?: string): Promise<{
    onboarding_id: number;
    review_status: string;
    material_id: number;
  }> {
    const response: AxiosResponse<{
      success: boolean;
      data: { onboarding_id: number; review_status: string; material_id: number };
      message: string;
    }> = await this.api.post(`/clients/group-onboarding/${onboardingId}/approve/`, {
      notes: notes || '',
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to approve workout plan');
    }
    return response.data.data;
  }

  async trainerRegenerateWorkoutPlan(
    onboardingId: number,
    trainerContext: string,
    notes?: string,
  ): Promise<{ workout_plan: { status: string; task_id: string | null } }> {
    const response: AxiosResponse<{
      success: boolean;
      data: { workout_plan: { status: string; task_id: string | null } };
      message: string;
    }> = await this.api.post(`/clients/group-onboarding/${onboardingId}/regenerate/`, {
      trainer_context: trainerContext,
      notes: notes || '',
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to regenerate workout plan');
    }
    return response.data.data;
  }

  async previewImportStripeCustomers(): Promise<{ 
    success: boolean;
    data: {
      customers_found: number;
      customers_existing: number;
      customers_to_import: number;
      subscriptions_to_sync: number;
      customers_preview: Array<{
        email: string;
        name: string;
        stripe_customer_id: string;
        trainer_name: string;
        subscriptions: Array<{
          id: string;
          status: string;
          price_id: string;
          product_id: string | null;
        }>;
      }>;
    };
  }> {
    const response: AxiosResponse<{
      success: boolean;
      data: {
        customers_found: number;
        customers_existing: number;
        customers_to_import: number;
        subscriptions_to_sync: number;
        customers_preview: Array<{
          email: string;
          name: string;
          stripe_customer_id: string;
          trainer_name: string;
          subscriptions: Array<{
            id: string;
            status: string;
            price_id: string;
            product_id: string;
          }>;
        }>;
      };
    }> = await this.api.get('/subscriptions/api/v1/preview-import-customers/');
    return response.data;
  }

  async importStripeCustomers(): Promise<{ 
    success: boolean;
    message: string;
    data: {
      customers_found: number;
      customers_existing: number;
      customers_created: number;
      subscriptions_synced: number;
      emails_sent: number;
      emails_failed: number;
      errors: string[];
    };
  }> {
    const response: AxiosResponse<{
      success: boolean;
      message: string;
      data: {
        customers_found: number;
        customers_existing: number;
        customers_created: number;
        subscriptions_synced: number;
        emails_sent: number;
        emails_failed: number;
        errors: string[];
      };
    }> = await this.api.post('/subscriptions/api/v1/import-customers/');
    return response.data;
  }

  async createStripeSubscription(clientData: { email: string; name: string; tier: string }): Promise<{ checkout_url: string }> {
    const response: AxiosResponse<{ checkout_url: string }> = await this.api.post('/stripe/create-subscription/', clientData);
    return response.data;
  }

  // Subscription Management
  async getSubscriptionOptions(currency?: string): Promise<SubscriptionOption[]> {
    const params = new URLSearchParams();
    if (currency) params.append('currency', currency);
    
    const response: AxiosResponse<SubscriptionOption[]> = await this.api.get(`/subscriptions/api/v1/options/?${params}`);
    return response.data;
  }

  async getSubscriptionOptionsByCurrency(currency: string): Promise<SubscriptionOption[]> {
    const response: AxiosResponse<SubscriptionOption[]> = await this.api.get(`/subscriptions/api/v1/options/by_currency/?currency=${currency}`);
    return response.data;
  }

  async getStripeProducts(page = 1, name?: string): Promise<PaginatedResponse<StripeProduct>> {
    const params = new URLSearchParams();
    if (page > 1) params.append('page', page.toString());
    if (name) params.append('name', name);

    const response: AxiosResponse<PaginatedResponse<StripeProduct>> = await this.api.get(`/subscriptions/api/v1/products/?${params}`);
    return response.data;
  }

  async getStripePrices(page = 1, productId?: string, currency?: string, interval?: string): Promise<PaginatedResponse<StripePrice>> {
    const params = new URLSearchParams();
    if (page > 1) params.append('page', page.toString());
    if (productId) params.append('product_id', productId);
    if (currency) params.append('currency', currency);
    if (interval) params.append('interval', interval);

    const response: AxiosResponse<PaginatedResponse<StripePrice>> = await this.api.get(`/subscriptions/api/v1/prices/?${params}`);
    return response.data;
  }

  async getSubscriptions(page = 1, clientId?: number, status?: string, activeOnly?: boolean): Promise<PaginatedResponse<Subscription>> {
    const params = new URLSearchParams();
    if (page > 1) params.append('page', page.toString());
    if (clientId) params.append('client_id', clientId.toString());
    if (status) params.append('status', status);
    if (activeOnly) params.append('active_only', 'true');

    const response: AxiosResponse<PaginatedResponse<Subscription>> = await this.api.get(`/subscriptions/api/v1/subscriptions/?${params}`);
    return response.data;
  }

  async getSubscription(id: number): Promise<Subscription> {
    const response: AxiosResponse<Subscription> = await this.api.get(`/subscriptions/api/v1/subscriptions/${id}/`);
    return response.data;
  }

  async createSubscription(data: { client_id: number; price_id: string; metadata?: Record<string, any> }): Promise<Subscription> {
    const response: AxiosResponse<Subscription> = await this.api.post('/subscriptions/api/v1/subscriptions/create_stripe_subscription/', data);
    return response.data;
  }

  async updateSubscription(id: number, data: { price_id: string }): Promise<Subscription> {
    const response: AxiosResponse<Subscription> = await this.api.post(`/subscriptions/api/v1/subscriptions/${id}/update_stripe_subscription/`, data);
    return response.data;
  }

  async cancelSubscription(id: number, data: { cancel_at_period_end: boolean; reason?: string }): Promise<Subscription> {
    const response: AxiosResponse<Subscription> = await this.api.post(`/subscriptions/api/v1/subscriptions/${id}/cancel_stripe_subscription/`, data);
    return response.data;
  }

  async getSubscriptionEvents(page = 1, eventType?: string, subscriptionId?: number, processed?: boolean): Promise<PaginatedResponse<SubscriptionEvent>> {
    const params = new URLSearchParams();
    if (page > 1) params.append('page', page.toString());
    if (eventType) params.append('event_type', eventType);
    if (subscriptionId) params.append('subscription_id', subscriptionId.toString());
    if (processed !== undefined) params.append('processed', processed.toString());

    const response: AxiosResponse<PaginatedResponse<SubscriptionEvent>> = await this.api.get(`/subscriptions/api/v1/events/?${params}`);
    return response.data;
  }

  // Stripe Sync
  async syncStripeProductsAndPrices(): Promise<SyncResult> {
    const response: AxiosResponse<SyncResult> = await this.api.post('/subscriptions/api/v1/sync/sync_products_and_prices/');
    return response.data;
  }

  async syncStripeSubscriptions(): Promise<SyncResult> {
    const response: AxiosResponse<SyncResult> = await this.api.post('/subscriptions/api/v1/sync/sync_subscriptions/');
    return response.data;
  }

  async syncAllStripeData(): Promise<SyncResult> {
    const response: AxiosResponse<SyncResult> = await this.api.post('/subscriptions/api/v1/sync/sync_all/');
    return response.data;
  }

  // Group Training Plans API
  async getGroupTrainingPlans(): Promise<GroupTrainingPlan[]> {
    const response: AxiosResponse<{ success: boolean; data: GroupTrainingPlan[]; message: string }> = 
      await this.api.get('/clients/group-training-plans/');
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch group training plans');
    }
    return response.data.data || [];
  }

  async getGroupTrainingPlan(id: number): Promise<GroupTrainingPlan> {
    const response: AxiosResponse<{ success: boolean; data: GroupTrainingPlan; message: string }> =
      await this.api.get(`/clients/group-training-plans/${id}/`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch group training plan');
    }
    return response.data.data;
  }

  async createGroupTrainingPlan(data: Partial<GroupTrainingPlan>): Promise<GroupTrainingPlan> {
    const response: AxiosResponse<{ success: boolean; data: GroupTrainingPlan; message: string }> =
      await this.api.post('/clients/group-training-plans/', data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create group training plan');
    }
    return response.data.data;
  }

  async updateGroupTrainingPlan(id: number, data: Partial<GroupTrainingPlan>): Promise<GroupTrainingPlan> {
    const response: AxiosResponse<{ success: boolean; data: GroupTrainingPlan; message: string }> =
      await this.api.patch(`/clients/group-training-plans/${id}/`, data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update group training plan');
    }
    return response.data.data;
  }

  async deleteGroupTrainingPlan(id: number): Promise<void> {
    await this.api.delete(`/clients/group-training-plans/${id}/`);
  }

  async getClientGroupTrainingPlans(clientId: number): Promise<GroupTrainingPlan[]> {
    const response: AxiosResponse<{ success: boolean; data: GroupTrainingPlan[]; message: string }> =
      await this.api.get(`/clients/group-training-plans/`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch client group training plans');
    }
    // Filter by client_id on frontend since backend returns all trainer's group plans
    const allPlans = response.data.data || [];
    return allPlans.filter(plan => plan.client_id === clientId || plan.client?.id === clientId);
  }

  // Exercise API
  async getExercises(): Promise<Exercise[]> {
    const response: AxiosResponse<{ success: boolean; data: Exercise[]; message: string }> =
      await this.api.get('/clients/exercises/');
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch exercises');
    }
    return response.data.data || [];
  }

  async getExercise(id: number): Promise<Exercise> {
    const response: AxiosResponse<{ success: boolean; data: Exercise; message: string }> =
      await this.api.get(`/clients/exercises/${id}/`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch exercise');
    }
    return response.data.data;
  }

  // Training Plan Exercise API
  async getTrainingPlanExercises(trainingPlanId: number): Promise<TrainingPlanExercise[]> {
    const response: AxiosResponse<{ success: boolean; data: TrainingPlanExercise[]; message: string }> =
      await this.api.get(`/clients/training-plan-exercises/?training_plan_id=${trainingPlanId}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch training plan exercises');
    }
    return response.data.data || [];
  }

  async createTrainingPlanExercise(data: Partial<TrainingPlanExercise>): Promise<TrainingPlanExercise> {
    const response: AxiosResponse<{ success: boolean; data: TrainingPlanExercise; message: string }> =
      await this.api.post('/clients/training-plan-exercises/', data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create training plan exercise');
    }
    return response.data.data;
  }

  async updateTrainingPlanExercise(id: number, data: Partial<TrainingPlanExercise>): Promise<TrainingPlanExercise> {
    const response: AxiosResponse<{ success: boolean; data: TrainingPlanExercise; message: string }> =
      await this.api.patch(`/clients/training-plan-exercises/${id}/`, data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update training plan exercise');
    }
    return response.data.data;
  }

  async deleteTrainingPlanExercise(id: number): Promise<void> {
    await this.api.delete(`/clients/training-plan-exercises/${id}/`);
  }

  // Group Zoom Sessions API
  async getGroupZoomSessions(): Promise<GroupZoomSession[]> {
    const response: AxiosResponse<{ success: boolean; data: GroupZoomSession[]; message: string }> = 
      await this.api.get('/clients/group-zoom-sessions/');
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch group zoom sessions');
    }
    return response.data.data || [];
  }

  async getGroupZoomSession(id: number): Promise<GroupZoomSession> {
    const response: AxiosResponse<{ success: boolean; data: GroupZoomSession; message: string }> = 
      await this.api.get(`/clients/group-zoom-sessions/${id}/`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch group zoom session');
    }
    return response.data.data;
  }

  async createGroupZoomSession(data: {
    client_id: number;
    session_date: string;
    zoom_link?: string;
    meeting_id?: string;
    status?: 'scheduled' | 'completed' | 'cancelled';
    notes?: string;
  }): Promise<GroupZoomSession> {
    const response: AxiosResponse<{ success: boolean; data: GroupZoomSession; message: string }> = 
      await this.api.post('/clients/group-zoom-sessions/', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to create group zoom session');
    }
    return response.data.data;
  }

  async updateGroupZoomSession(id: number, data: Partial<{
    session_date: string;
    zoom_link: string;
    meeting_id: string;
    status: 'scheduled' | 'completed' | 'cancelled';
    notes: string;
  }>): Promise<GroupZoomSession> {
    const response: AxiosResponse<{ success: boolean; data: GroupZoomSession; message: string }> = 
      await this.api.patch(`/clients/group-zoom-sessions/${id}/`, data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to update group zoom session');
    }
    return response.data.data;
  }

  async deleteGroupZoomSession(id: number): Promise<void> {
    await this.api.delete(`/clients/group-zoom-sessions/${id}/`);
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService; 
